import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import {
  MAIN_THEME_COLOR,
  SECONDARY_THEME_COLOR,
} from "../../../providers/theme/colors/colors";
import {
  eventOptions,
  sanitizePhone,
  type EventFormState,
  type RegistrationEventId,
} from "../data";

type DycEventFormsProps = {
  selectedEvents: RegistrationEventId[];
  eventForms: EventFormState;
  onToggleEventSelection: (eventId: RegistrationEventId) => void;
  onSpotlightParticipantCountChange: (value: string) => void;
  onSpotlightParticipantNameChange: (index: number, value: string) => void;
  onJamChange: (field: "participantName" | "participantContact", value: string) => void;
  onEcoMosaicChange: (
    field:
      | "primaryName"
      | "primaryContact"
      | "secondaryName"
      | "secondaryContact"
      | "hasSecondParticipant",
    value: string | boolean,
  ) => void;
  onFaithVaultMemberNameChange: (index: number, value: string) => void;
  onFaithVaultTeamLeadContactChange: (value: string) => void;
  onRhythmRootsParticipantCountChange?: (value: string) => void;
  onRhythmRootsParticipantNameChange?: (index: number, value: string) => void;
  onRhythmRootsTeamLeadContactChange?: (value: string) => void;
  onSpotlightFieldChange: (
    field: "performingEventName" | "eventDescription" | "teamLeadContact",
    value: string,
  ) => void;
  onOpenPitchBattle?: () => void;
  isSubmitting: boolean;
  onSubmit: () => void;
};

const phoneInputProps = {
  inputMode: "numeric" as const,
  maxLength: 15,
};


export default function DycEventForms({
  selectedEvents,
  eventForms,
  onToggleEventSelection,
  onSpotlightParticipantCountChange,
  onSpotlightParticipantNameChange,
  onJamChange,
  onEcoMosaicChange,
  onFaithVaultMemberNameChange,
  onFaithVaultTeamLeadContactChange,
  onRhythmRootsParticipantCountChange,
  onRhythmRootsParticipantNameChange,
  onRhythmRootsTeamLeadContactChange,
  onSpotlightFieldChange,
  onOpenPitchBattle,
  isSubmitting,
  onSubmit,
}: DycEventFormsProps) {
  const selectedEventOptions = eventOptions.filter(
    (eventOption) => selectedEvents.includes(eventOption.id as RegistrationEventId),
  );

  return (
    <>
      <Card
        sx={{
          borderRadius: 4,
          border: `1px solid ${SECONDARY_THEME_COLOR}44`,
          background:
            "linear-gradient(180deg, rgba(247,251,255,1) 0%, rgba(255,255,255,1) 100%)",
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h5" fontWeight={800} color={MAIN_THEME_COLOR}>
            Event Selection
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            Choose the events first. Event-specific forms will appear below.
          </Typography>
          <Grid container spacing={1.5} mt={1}>
            {eventOptions.map((eventOption) => (
              <Grid item xs={12} sm={6} md={4} key={eventOption.id}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedEvents.includes(eventOption.id as RegistrationEventId)}
                      onChange={() =>
                        onToggleEventSelection(eventOption.id as RegistrationEventId)
                      }
                    />
                  }
                  label={eventOption.label}
                />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {selectedEventOptions.map((eventOption) => {
        const eventId = eventOption.id as RegistrationEventId;

        if (eventId === "pitchbattle") {
          return (
            <Card
              key={eventId}
              sx={{
                borderRadius: 4,
                border: `1px solid ${SECONDARY_THEME_COLOR}44`,
                backgroundColor: "#fffcf7",
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box
                  display="flex"
                  flexDirection={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  gap={2}
                >
                  <Box>
                    <Typography variant="h5" fontWeight={800} color={MAIN_THEME_COLOR}>
                      Pitch Battle (Selected)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                      Participation confirmed! Pitch Battle submissions use a separate form with multiple entries allowed per unit.
                    </Typography>
                  </Box>
                  {onOpenPitchBattle ? (
                    <Button
                      variant="contained"
                      onClick={onOpenPitchBattle}
                      sx={{
                        borderRadius: 999,
                        px: 3,
                        py: 1,
                        whiteSpace: "nowrap",
                        backgroundColor: MAIN_THEME_COLOR,
                        "&:hover": {
                          backgroundColor: MAIN_THEME_COLOR,
                        },
                      }}
                    >
                      Go to Pitch Battle Form
                    </Button>
                  ) : null}
                </Box>
              </CardContent>
            </Card>
          );
        }

        if (eventId === "jam") {
          return (
            <Card key={eventId} sx={{ borderRadius: 4, border: `1px solid ${MAIN_THEME_COLOR}22` }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h5" fontWeight={800} color={MAIN_THEME_COLOR}>
                  {eventOption.label}
                </Typography>
                <Grid container spacing={3} mt={0.5}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Participant Name"
                      value={eventForms.jam.participantName}
                      onChange={(event) => onJamChange("participantName", event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Contact Number"
                      value={eventForms.jam.participantContact}
                      onChange={(event) =>
                        onJamChange("participantContact", sanitizePhone(event.target.value))
                      }
                      inputProps={phoneInputProps}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          );
        }

        if (eventId === "ecomosaic") {
          return (
            <Card key={eventId} sx={{ borderRadius: 4, border: `1px solid ${MAIN_THEME_COLOR}22` }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h5" fontWeight={800} color={MAIN_THEME_COLOR}>
                  {eventOption.label}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Add one participant by default. You can include one more individual if needed.
                </Typography>
                <Grid container spacing={3} mt={0.5}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Participant 1 Name"
                      value={eventForms.ecomosaic.primaryName}
                      onChange={(event) =>
                        onEcoMosaicChange("primaryName", event.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Participant 1 Contact"
                      value={eventForms.ecomosaic.primaryContact}
                      onChange={(event) =>
                        onEcoMosaicChange("primaryContact", sanitizePhone(event.target.value))
                      }
                      inputProps={phoneInputProps}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={eventForms.ecomosaic.hasSecondParticipant}
                          onChange={(event) =>
                            onEcoMosaicChange(
                              "hasSecondParticipant",
                              event.target.checked,
                            )
                          }
                        />
                      }
                      label="Add another individual"
                    />
                  </Grid>
                  {eventForms.ecomosaic.hasSecondParticipant ? (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label="Participant 2 Name"
                          value={eventForms.ecomosaic.secondaryName}
                          onChange={(event) =>
                            onEcoMosaicChange("secondaryName", event.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label="Participant 2 Contact"
                          value={eventForms.ecomosaic.secondaryContact}
                          onChange={(event) =>
                            onEcoMosaicChange(
                              "secondaryContact",
                              sanitizePhone(event.target.value),
                            )
                          }
                          inputProps={phoneInputProps}
                        />
                      </Grid>
                    </>
                  ) : null}
                </Grid>
              </CardContent>
            </Card>
          );
        }

        if (eventId === "spotlight") {
          const participantCount = Number(eventForms.spotlight.participantCount || "1");
          return (
            <Card key={eventId} sx={{ borderRadius: 4, border: `1px solid ${MAIN_THEME_COLOR}22` }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h5" fontWeight={800} color={MAIN_THEME_COLOR}>
                  {eventOption.label}
                </Typography>
                <Grid container spacing={3} mt={0.5}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Performing Event Name"
                      value={eventForms.spotlight.performingEventName}
                      onChange={(event) =>
                        onSpotlightFieldChange("performingEventName", event.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Team Lead Contact No"
                      value={eventForms.spotlight.teamLeadContact}
                      onChange={(event) =>
                        onSpotlightFieldChange(
                          "teamLeadContact",
                          sanitizePhone(event.target.value),
                        )
                      }
                      inputProps={phoneInputProps}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <TextField
                        select
                        required
                        label="Number of Participants"
                        value={eventForms.spotlight.participantCount}
                        onChange={(event) =>
                          onSpotlightParticipantCountChange(event.target.value)
                        }
                      >
                        {["1", "2", "3", "4", "5"].map((count) => (
                          <MenuItem key={count} value={count}>
                            {count}
                          </MenuItem>
                        ))}
                      </TextField>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      required
                      multiline
                      rows={3}
                      label="Event Description"
                      placeholder="Briefly describe your performance/event..."
                      value={eventForms.spotlight.eventDescription}
                      onChange={(event) =>
                        onSpotlightFieldChange("eventDescription", event.target.value)
                      }
                    />
                  </Grid>
                  {Array.from({ length: participantCount }).map((_, index) => (
                    <Grid item xs={12} md={6} key={`spotlight-member-${index + 1}`}>
                      <TextField
                        fullWidth
                        required
                        label={`Participant ${index + 1} Name`}
                        value={eventForms.spotlight.participantNames[index] || ""}
                        onChange={(event) =>
                          onSpotlightParticipantNameChange(index, event.target.value)
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          );
        }

        if (eventId === "rhythmroots") {
          const rawCount = Number(eventForms.rhythmroots.participantCount || "1");
          const rhythmCount = isNaN(rawCount) || rawCount < 1 ? 1 : rawCount > 50 ? 50 : rawCount;
          return (
            <Card key={eventId} sx={{ borderRadius: 4, border: `1px solid ${MAIN_THEME_COLOR}22` }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                  <Typography variant="h5" fontWeight={800} color={MAIN_THEME_COLOR}>
                    Rhythm and Roots
                  </Typography>
                  <Typography variant="caption" fontWeight={700} color="#ed6c02" sx={{ backgroundColor: "#fff4e5", px: 1.5, py: 0.5, borderRadius: 999 }}>
                    Minimum: 1 Participant
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Enter the participant details for Rhythm and Roots.
                </Typography>
                <Grid container spacing={3} mt={0.5}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      type="number"
                      label="Minimum 1 Participant from unit"
                      value={eventForms.rhythmroots.participantCount}
                      onChange={(event) => {
                        const val = event.target.value.replace(/\D/g, "");
                        if (val !== "" && Number(val) > 50) return;
                        onRhythmRootsParticipantCountChange?.(val);
                      }}
                      inputProps={{
                        min: 1,
                        max: 50,
                      }}
                      helperText="Minimum 1 required"
                    />
                  </Grid>
                  {Array.from({ length: rhythmCount }).map((_, index) => (
                    <Grid item xs={12} md={6} key={`rhythmroots-member-${index + 1}`}>
                      <TextField
                        fullWidth
                        required
                        label={`Participant ${index + 1} Name`}
                        value={eventForms.rhythmroots.participantNames[index] || ""}
                        onChange={(event) =>
                          onRhythmRootsParticipantNameChange?.(index, event.target.value)
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          );
        }

        return (
          <Card key={eventId} sx={{ borderRadius: 4, border: `1px solid ${MAIN_THEME_COLOR}22` }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="h5" fontWeight={800} color={MAIN_THEME_COLOR}>
                {eventOption.label}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                Three member team is required.
              </Typography>
              <Grid container spacing={3} mt={0.5}>
                {eventForms.faithvault.memberNames.map((memberName, index) => (
                  <Grid item xs={12} md={6} key={`faithvault-member-${index + 1}`}>
                    <TextField
                      fullWidth
                      required
                      label={`Member ${index + 1} Name`}
                      value={memberName}
                      onChange={(event) =>
                        onFaithVaultMemberNameChange(index, event.target.value)
                      }
                    />
                  </Grid>
                ))}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Team Lead Contact No"
                    value={eventForms.faithvault.teamLeadContact}
                    onChange={(event) =>
                      onFaithVaultTeamLeadContactChange(sanitizePhone(event.target.value))
                    }
                    inputProps={phoneInputProps}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );
      })}

      <Box display="flex" justifyContent="center">
        <Button
          variant="contained"
          size="large"
          disabled={isSubmitting}
          onClick={onSubmit}
          sx={{
            minWidth: 220,
            py: 1.4,
            borderRadius: 999,
            background: `linear-gradient(90deg, ${MAIN_THEME_COLOR} 0%, ${SECONDARY_THEME_COLOR} 100%)`,
          }}
        >
          {isSubmitting ? "Submitting..." : "Final Submission"}
        </Button>
      </Box>
    </>
  );
}
